import { AutocompleteWithLabel } from '@adotel/components'
import {
  useLazyQuery,
  useMutation,
  useQuery,
  useSubscription
} from '@apollo/client'
import {
  Button,
  CircularProgress,
  Collapse,
  Hidden,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import FilterListIcon from '@material-ui/icons/FilterList'
import PaymentIcon from '@material-ui/icons/Payment'
import { Pagination } from '@material-ui/lab'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import WalletCart from '../../components/WalletCart'
import { WALLET_QUERY } from '../../graphql/wallet'
import { CREDITORS_BRIEF_QUERY } from '../../graphql/walletCreditors'
import { CREATE_PROFORMA_MUTATION } from '../../graphql/walletPaymemt'
import {
  ALL_SETTLEMENT_TRANSACTIONS_WITHOUT_UPCOMING,
  CALCULATE_ALL_TRANSACTIONS_JOB_WITHOUT_UPCOMING_SUB,
  SETTLEMENT_TRANSACTIONS_QUERY
} from '../../graphql/walletTransactions'
import { getTranslate, lang } from '../../localization'
import { Wallet } from '../../types/wallet'
import { CreditorBrief } from '../../types/walletCreditor'
import { SortBy } from '../../types/walletEnums'
import {
  PaymentTransactionFilterInput,
  PaymentTransactionResponse,
  SelectedTransactions
} from '../../types/walletTransaction'
import { SettlementFilterBox } from './SettlementFilterBox'
import { WalletTransactionListTableDesktop } from './WalletTransactionListTableDesktop'
import useStyles from './styles/index.styles'

type Props = {}

export default function WalletSettlement({}: Props) {
  const classes = useStyles()
  const theme = useTheme()
  const params = useParams<{ walletId: string }>()
  const history = useHistory()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [trackingID, setTrackingID] = useState<string | null>(null)
  const [outstandingBalance, setOutstandingBalance] = useState<number>(0)
  const [
    waitingForWebSocketToCalcAllTransactions,
    setwaitingForWebSocketToCalcAllTransactions
  ] = useState(false)
  const [
    waitingForWebSocketToCalcOutStandingBalance,
    setwaitingForWebSocketToCalcOutStandingBalance
  ] = useState(false)

  const { data: wallet } = useQuery<{
    wallet: Wallet
  }>(WALLET_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      walletID: params.walletId
    }
  })
  const [autoCompleteLabelForCreditor, setautoCompleteLabelForCreditor] =
    useState<any>(null)
  const [getCreditors, { data: creditorsData, loading: loadingForCreditors }] =
    useLazyQuery<{
      creditorsBrief: CreditorBrief[]
    }>(CREDITORS_BRIEF_QUERY, { notifyOnNetworkStatusChange: true })

  useEffect(() => {
    if (wallet?.wallet.isBilateral) {
      getCreditors({
        variables: {
          walletID: params.walletId
        }
      })
    }
  }, [wallet])
  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(false)
  const perPage = 6
  const [page, setpage] = useState<number>(1)
  const [filters, setfilters] = useState<PaymentTransactionFilterInput>({
    walletID: params.walletId,
    page: page,
    perPage: perPage
  })

  const [startJob] = useLazyQuery(
    ALL_SETTLEMENT_TRANSACTIONS_WITHOUT_UPCOMING,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        const id = data?.allSettlementTransactionsWithoutUpcoming?.trackingID
        if (id) setTrackingID(id)
      }
    }
  )

  const { data: allTransactionsToPayWithoutUpcoming } = useSubscription(
    CALCULATE_ALL_TRANSACTIONS_JOB_WITHOUT_UPCOMING_SUB,
    {
      variables: { trackingID },
      skip: !trackingID
    }
  )

  const [sortBy, setsortBy] = useState<SortBy>()
  const [selectedTransactions, setselectedTransactions] =
    useState<SelectedTransactions>({
      totalAmountToPay: 0,
      transactionIDs: []
    })
  const [
    getTransactions,
    { data: transactionsData, loading: loadingForTransactions, refetch }
  ] = useLazyQuery<{
    settlementTransactions: PaymentTransactionResponse
  }>(SETTLEMENT_TRANSACTIONS_QUERY, {
    notifyOnNetworkStatusChange: true
  })
  useEffect(() => {
    if (
      (wallet && !wallet.wallet.isBilateral) ||
      filters.creditorID !== undefined
    ) {
      getTransactions({
        variables: {
          filters: filters,
          sortBy: sortBy
        }
      })
    }
  }, [sortBy, filters.creditorID])
  useEffect(() => {
    const {
      page: unusedPage,
      perPage: unusedPerPage,
      ...filtersWithoutPagination
    } = filters

    startJob({
      variables: {
        filters: filtersWithoutPagination,
        sortBy
      }
    })
    setwaitingForWebSocketToCalcOutStandingBalance(true)
  }, [])

  useEffect(() => {
    if (
      allTransactionsToPayWithoutUpcoming
        ?.calculateAllTransactionsWithoutUpcomingJobHandler?.status ===
      'Completed'
    ) {
      const total =
        allTransactionsToPayWithoutUpcoming
          .calculateAllTransactionsWithoutUpcomingJobHandler.totalAmount
      setOutstandingBalance(total)
      setwaitingForWebSocketToCalcOutStandingBalance(false)
    }
  }, [allTransactionsToPayWithoutUpcoming])

  const [createProforma] = useMutation(CREATE_PROFORMA_MUTATION, {
    onCompleted: (resp) => {
      if (resp.createProforma.proformaNumber) {
        toast.info(getTranslate(`${resp.createProforma.message}`))
        history.push(`/${lang}/adopay/wallet/${params.walletId}/invoice-list`)
      }
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  return (
    <div>
      <PageHeader title={getTranslate('Settlement')} />
      <div className={classes.header}>
        <WalletCart
          icon={<AccountBalanceWalletIcon className={classes.color} />}
          title={getTranslate('Used Credit')}
          balance={
            wallet
              ? wallet?.wallet.maxCredit - wallet?.wallet.remainingCreditBalance
              : 0
          }
          currency={'IRR'}
          cashBalance
        />
        <WalletCart
          icon={<AccessTimeIcon className={classes.color} />}
          title={getTranslate('Outstanding Balance')}
          balance={outstandingBalance}
          currency={'IRR'}
          cashBalance
          loading={waitingForWebSocketToCalcOutStandingBalance}
        />
      </div>
      <div className={classes.filterButtonContainer}>
        {wallet?.wallet.isBilateral && (
          <div className={classes.leftBtnContainer}>
            <AutocompleteWithLabel
              label={getTranslate('Creditor')}
              placeholder={getTranslate('Select Creditor')}
              includeInputInList
              fullWidth
              onChange={(event, v: any) => {
                if (v) {
                  setautoCompleteLabelForCreditor({
                    value: v.value,
                    label: v.label,
                    code: v.code
                  })
                  setfilters({
                    ...filters,
                    creditorID: v.value
                  })
                } else {
                  setautoCompleteLabelForCreditor({
                    value: '',
                    label: '',
                    code: ''
                  })
                  setfilters({
                    ...filters,
                    creditorID: undefined
                  })
                }
              }}
              getOptionSelected={(option, value) =>
                option.value === value.value && option.label === value.label
              }
              getOptionLabel={(option) => option.label || ''}
              options={
                creditorsData
                  ? creditorsData?.creditorsBrief.map((item) => ({
                      label: item.entity.entityName,
                      value: item.creditorID.toString() || ''
                    }))
                  : []
              }
              value={autoCompleteLabelForCreditor}
              renderOption={(option) => <>{option.label}</>}
              size="small"
              inputSize="small"
              loading={loadingForCreditors}
              loadingText={getTranslate('Please wait...')}
            />
            {selectedTransactions.totalAmountToPay > 0 ||
            waitingForWebSocketToCalcAllTransactions ? (
              <Button
                size={isSmallScreen ? 'large' : 'small'}
                startIcon={
                  waitingForWebSocketToCalcAllTransactions ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <PaymentIcon />
                  )
                }
                variant="contained"
                color="secondary"
                disabled={waitingForWebSocketToCalcAllTransactions}
                onClick={() =>
                  createProforma({
                    variables: {
                      transactionIDs: selectedTransactions.transactionIDs
                    }
                  })
                }>
                {isSmallScreen
                  ? ''
                  : waitingForWebSocketToCalcAllTransactions
                  ? getTranslate('Calculating Total...')
                  : getTranslate('Pay Selected') +
                    ' | ' +
                    selectedTransactions.totalAmountToPay +
                    ' ' +
                    wallet?.wallet.currency}
              </Button>
            ) : (
              <div />
            )}
          </div>
        )}
        <Button
          className={classes.filterBtn}
          size={isSmallScreen ? 'large' : 'small'}
          startIcon={<FilterListIcon />}
          variant="outlined"
          color="primary"
          onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
          {isSmallScreen ? '' : getTranslate('Filter')}
        </Button>
      </div>
      <Hidden smDown>
        <Collapse in={filterButtonSelected}>
          <SettlementFilterBox
            filters={filters}
            setfilters={setfilters}
            setPage={setpage}
            getTransactions={getTransactions}
            loadingForTransactions={loadingForTransactions}
            sortBy={sortBy}
            startJob={startJob}
            setwaitingForWebSocketToCalcOutStandingBalance={
              setwaitingForWebSocketToCalcOutStandingBalance
            }
          />
        </Collapse>
        {((wallet && !wallet.wallet.isBilateral) ||
          filters.creditorID !== undefined) && (
          <>
            <Hidden smDown>
              <WalletTransactionListTableDesktop
                transactionsData={
                  transactionsData?.settlementTransactions.transactions || []
                }
                setSort={setsortBy}
                sort={sortBy}
                {...{
                  refetch,
                  setpage,
                  loadingForTransactions,
                  selectedTransactions,
                  setselectedTransactions,
                  filters,
                  setwaitingForWebSocketToCalcAllTransactions
                }}
              />
            </Hidden>

            <div className={classes.paginationContainer}>
              <Pagination
                count={
                  transactionsData &&
                  (transactionsData.settlementTransactions.total % perPage === 0
                    ? transactionsData.settlementTransactions.total / perPage
                    : Math.floor(
                        transactionsData.settlementTransactions.total / perPage
                      ) + 1)
                }
                color="primary"
                page={page}
                onChange={(_, value) => {
                  setpage(value)
                  const newFilters = { ...filters, page: value }
                  setfilters(newFilters)
                  if (
                    !wallet?.wallet.isBilateral ||
                    filters.creditorID !== undefined
                  ) {
                    getTransactions({
                      variables: {
                        filters: newFilters,
                        sortBy: sortBy
                      }
                    })
                  }
                }}
              />
            </div>
          </>
        )}
      </Hidden>
    </div>
  )
}
